<template>
  <div v-bind="$attrs">
    <v-checkbox
      :id="id"
      ref="so-checkbox"
      v-model="selected"
      :label="label"
      :disabled="disabled"
      :indeterminate="indeterminate"
      :multiple="multiple"
      :value="value"
      :error="!!error"
      :error-messages="error ? [error] : []"
      :rules="rules"
      :false-value="falseValue"
      hide-details="auto"
      density="compact"
      color="success"
      validate-on="input"
      data-testid="so-checkbox"
      :class="{
        'top-aligned': topAligned,
        'truncate-text': truncateText,
      }"
      :data-dd-action-name="dataDdActionName ?? label"
      @click.stop
    >
      <template v-if="$slots.label" #label>
        <slot name="label" />
      </template>
    </v-checkbox>
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import { ValidationRule } from "@/types/ValidationRule"

  export type SoCheckboxValue = string | number | boolean
  export default defineComponent({
    inheritAttrs: false,
    props: {
      id: {
        type: String,
        default: undefined,
      },
      label: {
        type: String,
        default: null,
      },
      modelValue: {
        type: [Array, Number, Boolean, String] as PropType<
          SoCheckboxValue[] | SoCheckboxValue | null
        >,
        default: null,
      },
      value: {
        type: [String, Number, Boolean] as PropType<SoCheckboxValue>,
        default: undefined,
      },
      falseValue: {
        type: [String, Number, Boolean] as PropType<SoCheckboxValue | null>,
        default: undefined,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      indeterminate: {
        type: Boolean,
        default: false,
      },
      multiple: {
        type: Boolean,
        default: false,
      },
      error: {
        type: String,
        default: undefined,
      },
      rules: {
        type: Array as PropType<ValidationRule[]>,
        default: [] as ValidationRule[],
      },
      topAligned: {
        type: Boolean,
        default: false,
      },
      truncateText: {
        type: Boolean,
        default: false,
      },
      dataDdActionName: {
        type: String,
        default: null,
      },
    },
    emits: ["update:modelValue"],
    computed: {
      selected: {
        get() {
          return this.modelValue
        },
        set(val: Array<string> | boolean) {
          this.$emit("update:modelValue", val)
        },
      },
    },
  })
</script>
<style scoped>
  :deep(.v-selection-control__wrapper) {
    height: 20px;
    width: 20px;
  }
  :deep(.v-selection-control > .v-label) {
    width: auto;
  }
  :deep(.v-selection-control__input) {
    border: 1px solid rgb(var(--v-theme-on-surface));
    border-radius: 4px;
    height: 20px;
    width: 20px;
    overflow: hidden;
  }
  :deep(.v-selection-control--focus-visible > .v-selection-control__wrapper) {
    outline: var(--theme-outline);
    border-radius: var(--theme-radius);
  }
  :deep(.v-label) {
    margin-left: 8px;
  }
  :deep(.v-input__details) {
    padding: 0px;
    min-height: 0px;
  }
  .top-aligned :deep(.v-selection-control__wrapper) {
    align-self: flex-start;
    margin-top: 5px;
  }
  .truncate-text :deep(label) {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: inline-block;
    line-height: var(--v-input-control-height);
  }
  .truncate-text :deep(.v-selection-control) {
    width: 100%;
  }
  :deep(.v-selection-control i) {
    display: none;
  }
  :deep(.v-selection-control--dirty i) {
    display: block;
  }
</style>
